/**
 * Profile
 *
 * Displays the current user's profile card including avatar, name, role, and email,
 * their departments, and an at-a-glance list of permissions. Also shows the app
 * version and provides a logout action using MSAL's redirect flow. Loading state
 * is surfaced via the global appearance hook for consistency across the app.
 */
import React, { useEffect, useMemo, useState } from "react";
import "./profile.scss";
// Adjust the path as necessary
import { useMsal } from "@azure/msal-react";
import { Badge, Typography, Button } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";

import { useNavigate } from "react-router-dom";
import { useSetLoading } from "../../common/hooks/appearance";
import { getEmployee } from "../../api/profile";
import { AlertComponent } from "../../common/components";
import ConfirmationDialog from "../../common/components/confirmation-dialog/confirmation-dialog";
import packageJson from "../../../../package.json";
import { routes } from "../../constants/routes";
import Avatar from "@mui/material/Avatar";
import ProfileDepartment from "../departments/profile-department";
import Permissions from "./Permissions";
import { usePermissions } from "../../common/hooks/usePermissions";
import MODULES from "../../constants/modules";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import Cookies from "js-cookie";

/**
 * Props for the Profile component.
 * - employeeId: Optional employee id to load; defaults to current user
 * - onClose: Optional callback typically used when Profile is rendered in a menu/drawer
 */
interface ProfileProps {
  employeeId?: number;
  onClose?: () => void;
}

const Profile: React.FC<ProfileProps> = ({ onClose }) => {
  const employeeDetails = localStorage.getItem("userDetails")
    ? {...JSON.parse(localStorage.getItem("userDetails") || "{}"), ...JSON.parse(Cookies.get("userDetails") || "{}")}
    : null;

  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [isRole, setIsRole] = useState<boolean>(true);
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const navigate = useNavigate();
  const { instance } = useMsal();
  const version = packageJson.version.split(".").slice(0, 3).join("."); // "1.0"
  const { canRead } = usePermissions(MODULES.ROLE_MANAGEMENT);

  useEffect(() => {
    if (employeeDetails?.roles?.length === 0) {
      setIsRole(false);
    }
  }, [employeeDetails]);

  /**
   * Initiates a logout redirect using the active MSAL account and navigates back to the landing page.
   * If no account is present, clears local storage and hard-redirects to the landing route.
   */
  const handleLogoutRedirect = () => {
    const accounts = instance.getAllAccounts();
    const hostUrl = globalThis.location.origin;
    const logoutRequest = {
      account: accounts?.[0],
      postLogoutRedirectUri: `${hostUrl}${routes.LANDING}`,
    };
    if (accounts.length > 0) {
      instance.logoutRedirect(logoutRequest);
      setTimeout(() => {
        localStorage.clear();
        Cookies.remove("userDetails");
      }, 100);
    } else {
      localStorage.clear();
      Cookies.remove("userDetails");
      globalThis.location.href = `${hostUrl}${routes.LANDING}`;
    }
  };

  /** Navigate to the Roles/Permissions dashboard. */
  const handleManagePermissions = () => {
    navigate(routes.ROLESDASHBOARD);
  };

  /** Fetches and normalizes the employee's profile details for display. */
  const fetchUserDetails = async (employeeId: number) => {
    setLoading(true);
    try {
      const response = await getEmployee(employeeId || 3);
      setProfileData({
        name: response?.data?.data?.employeeFullName,
        fullName: response?.data?.data?.employeeFullName
          ? `${response?.data?.data?.employeeFullName?.split(" ")[0][0]}${response?.data?.data?.employeeFullName?.split(" ")[1][0]}`
          : "",
        role: response?.data?.data?.roles?.length
          ? response?.data?.data?.roles
              ?.map((item: any) => item.name)
              .join(", ")
          : "",
        email: response?.data?.data?.emailId,
        image: response?.data?.data?.employeeImage || null,
        departments: [response?.data?.data?.departmentName || "Sales"],
      });
      setLoading(false);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch user details",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeDetails) {
      fetchUserDetails(employeeDetails.employeeId);
    }
  }, []);

  /**
   * Derives Avatar props with initials for a given full name.
   * Example: "Ada Lovelace" -> children: "AL" with a consistent background color.
   */
  const stringAvatar = (name: string) => {
    return {
      sx: {
        bgcolor: "#403c6f",
        width: 56,
        height: 56,
      },
      children: `${name?.split(" ")[0][0]}${name?.split(" ")[1][0]}`,
    };
  };
  const profilePhotoImage = localStorage.getItem("profileImageUrl") || null;
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  let profileAvatar: React.ReactNode = <Avatar />;
  if (profilePhotoImage) {
    profileAvatar = (
      <Avatar
        src={profilePhotoImage}
        sx={{
          bgcolor: "#403c6f",
          width: 56,
          height: 56,
        }}
      >
        {profileData?.fullName}
      </Avatar>
    );
  } else if (profileData?.name) {
    profileAvatar = <Avatar {...stringAvatar(profileData?.name || "")} />;
  }

  return (
    <>
      <OverlayScrollbarsComponent
        className="profile-wrapper"
        options={{
          scrollbars: {
            theme: "os-theme-dark",
            autoHide: "scroll",
            autoHideDelay: 400,
          },
        }}
      >
        {/* Profile Card */}
        <div className="profile-card">
          <div
            className="profile-header"
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            <Badge>{profileAvatar}</Badge>
            <div className="profile-info">
              <h3>{profileData?.name}</h3>
              <div className="role">
                {profileData?.role === "Super Admin"
                  ? "Admin"
                  : profileData?.role}
              </div>
              <div className="email">{profileData?.email}</div>
            </div>
          </div>
          <div className="profile-actions">
            <Typography
              variant="caption"
              sx={{ mt: 1, color: "#666", fontSize: "14px" }}
            >
              Version {version}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              sx={{
                borderRadius: 5,
                textTransform: "none",
                fontWeight: "bold",
                color: "#F15B59",
                px: 3,
              }}
              onClick={() => setIsOpenConfirmation(true)}
            >
              Logout
              <LogoutIcon
                sx={{ fontSize: 16, ml: 1, height: "17px", width: "17px" }}
              />
            </Button>
          </div>
        </div>

        {/* Department Card */}
        <div className="profile-card">
          <div className="section">
            <h4>Department</h4>
            <div className="departments">
              {profileData?.departments?.map((dept: any, idx: any) => (
                <ProfileDepartment key={idx + 1} department={dept} />
              ))}
            </div>
          </div>
        </div>

        {/* Permissions Card */}
        {isRole && (
          <div className="profile-card">
            <div className="section">
              <div className="permissions-header">
                <h4>Permissions</h4>
                {canRead && (
                  <button
                    className="btn-outline-primary small"
                    onClick={() => {
                      handleManagePermissions();
                      onClose?.();
                    }}
                  >
                    Manage Permissions
                  </button>
                )}
              </div>
              <Permissions permissions={employeeDetails?.permissions} />
            </div>
          </div>
        )}
      </OverlayScrollbarsComponent>
      <ConfirmationDialog
        open={isOpenConfirmation}
        onClose={() => setIsOpenConfirmation(false)}
        onConfirm={handleLogoutRedirect}
        title="Logout"
        message="Are you sure you want to log out?"
        confirmText="Logout"
        confirmButtonColor="error"
        icon={
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: "#FDECEC",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <LogoutIcon style={{ color: "#EB5757", fontSize: 32 }} />
          </div>
        }
      />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default Profile;
